import { platformApiBaseUrl } from "./platform-api-origin";

/**
 * The shape of a plaintext invite token: 32 random bytes, base64url. The same
 * pattern as the gateway's `INVITE_TOKEN_PATTERN`; a pasted code that does not
 * match is still being typed, and is not worth a request.
 */
export const INVITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;

/** Why an invite can no longer be used, as `GET /api/invites/preview` names it. */
export type UnusableInviteStatus = "used" | "expired" | "invalid";

interface PendingInvite {
  readonly status: "pending";
  /** `null` on an invite issued before invites named their recipient. */
  readonly email: string | null;
  readonly team: string | null;
}

export type InvitePreview =
  /** No account has the email: the person registers. */
  | (PendingInvite & { readonly account: "new" })
  /**
   * The email has an account, which signs in and joins instead of registering.
   * `currentTeam` is the team that account is in now, `null` in its personal
   * organization.
   */
  | (PendingInvite & {
      readonly account: "existing";
      readonly email: string;
      readonly currentTeam: { readonly name: string } | null;
    })
  | { readonly status: UnusableInviteStatus }
  /** platform-api did not answer or failed: an outage, not an answer about the invite. */
  | { readonly status: "unavailable" };

const UNUSABLE: ReadonlySet<string> = new Set(["used", "expired", "invalid"]);

const textOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

/**
 * Asks platform-api which address an invite names and whether it still works.
 * Any answer this client cannot read reports `unavailable`, which leaves the
 * form usable: the sign-up itself checks the invite again.
 */
export async function fetchInvitePreview(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<InvitePreview> {
  if (!INVITE_TOKEN_PATTERN.test(token)) return { status: "invalid" };
  try {
    const query = new URLSearchParams({ token });
    const response = await fetchImpl(`${platformApiBaseUrl}/api/invites/preview?${query}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!response.ok) return { status: "unavailable" };
    const body = (await response.json()) as {
      readonly status?: unknown;
      readonly email?: unknown;
      readonly organization?: { readonly name?: unknown } | null;
      readonly account?: unknown;
      readonly currentTeam?: { readonly name?: unknown } | null;
    };
    if (body.status === "pending") {
      const email = textOrNull(body.email);
      const team = textOrNull(body.organization?.name);
      if (body.account === "existing" && email !== null) {
        const currentTeam = textOrNull(body.currentTeam?.name);
        return {
          status: "pending",
          email,
          team,
          account: "existing",
          currentTeam: currentTeam === null ? null : { name: currentTeam },
        };
      }
      return { status: "pending", email, team, account: "new" };
    }
    if (typeof body.status === "string" && UNUSABLE.has(body.status)) {
      return { status: body.status as UnusableInviteStatus };
    }
    return { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  }
}

/** What the registration form shows in its email field. */
export interface RegistrationEmail {
  readonly email: string;
  /** The invite names its recipient's address, and the sign-up must use it. */
  readonly emailLocked: boolean;
}

/**
 * The invite's email is the only one the sign-up accepts, so it replaces
 * whatever was typed. The name is always the person's own to type.
 */
export function registrationEmail(
  preview: InvitePreview | undefined,
  draft: string,
): RegistrationEmail {
  const invited = preview?.status === "pending" ? preview.email : null;
  return { email: invited ?? draft, emailLocked: invited !== null };
}
